import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaTrash, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { decodeBase64 } from '../../common/Utils/base64';
import { useTenantScriptService } from '../../components/services/useTenantScriptService';
import { useToast } from '../../providers/ToastProvider/ToastContext';

type UploadFile = {
  file: File;
  status: 'Uploading' | 'Completed';
};

interface UploadScriptFormProps {
  isEdit?: boolean;
  scriptData?: any;
}

const UploadScriptForm: React.FC<UploadScriptFormProps> = ({ isEdit = false, scriptData }) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const { selectedTenantId } = useTenantSelection();
  const navigate = useNavigate();
  const { uploadTenantScript, updateTenantScript } = useTenantScriptService();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      scriptName: '',
      scriptDescription: '',
      files: [] as UploadFile[],
    },
    validationSchema: Yup.object({
      scriptName: Yup.string().required('Script name is required'),
      scriptDescription: Yup.string().required('Script description is required'),
      files: Yup.array().min(1, 'At least one file is required'),
    }),
    onSubmit: (values) => {
      handleUploadScript(values);
    },
    enableReinitialize: true, // <-- allows form values to reset when scriptData changes
  });

  // Prefill in edit mode
  useEffect(() => {
    if (isEdit && scriptData) {
      formik.setValues({
        scriptName: scriptData.scriptName || '',
        scriptDescription: decodeBase64(scriptData.scriptDescription) || '',
        files: scriptData.scriptFileName
          ? [
              {
                file: new File([], scriptData.scriptFileName), // empty dummy file
                status: 'Completed',
              },
            ]
          : [],
      });
    }
  }, [isEdit, scriptData]);

  const processFiles = (files: File[]) => {
    const newUploads: UploadFile[] = files.map((file) => ({
      file,
      status: 'Uploading',
    }));

    const combined = [...formik.values.files, ...newUploads];
    formik.setFieldValue('files', combined);
    setUploadingFiles(combined);

    setTimeout(() => {
      const updated = combined.map((f) => ({
        ...f,
        status: 'Completed',
      }));
      formik.setFieldValue('files', updated);
      setUploadingFiles(updated);
    }, 1500);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const removeFile = (index: number) => {
    const updated = [...formik.values.files];
    updated.splice(index, 1);
    setUploadingFiles(updated);
    formik.setFieldValue('files', updated);
  };

  const handleUploadScript = async (values: any) => {
    try {
      setIsSubmitting(true); // disable button

      const { scriptName, scriptDescription, files } = values;
      const tenantId = selectedTenantId;
      const validFiles = files.map((f: UploadFile) => f.file);

      if (isEdit && scriptData?.tenantScriptStoreID) {
        await updateTenantScript(tenantId as string, scriptData.tenantScriptStoreID, scriptName, scriptDescription, validFiles);
      } else {
        await uploadTenantScript(tenantId as string, scriptName, scriptDescription, validFiles);
      }

      setIsSuccess(true);
      addToast(`Script ${isEdit ? 'updated' : 'uploaded'} successfully!`, 'success', 4000);
      setTimeout(() => {
        setIsSuccess(false);
        navigate('/ScriptAutomation');
      }, 2000);
    } catch (error) {
      console.error('Error saving script:', error);
      addToast('Error saving script. Please try again.', 'error', 4000);
    } finally {
      setIsSubmitting(false); // re-enable button whether success or error
    }
  };

  return (
    <div className="bg-1D1D1D text-white p-6 rounded space-y-6 shadow-md">
      <form onSubmit={formik.handleSubmit}>
        {/* Upload Section */}
        <label className="block text-sm font-medium mb-1">Script file *</label>
        <div
          onDrop={handleFileDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border border-gray-700 rounded-lg p-8 text-center mb-4 bg-[#1c1c1c]"
        >
          <p className="text-red-500">
            <span className="underline cursor-pointer" onClick={() => document.getElementById('fileInput')?.click()}>
              Browse Files
            </span>
          </p>
          <p className="text-gray-500 text-sm mt-1">Supported Files: PowerShell, Bash, Batch files</p>
          <input
            id="fileInput"
            type="file"
            accept=".ps1,.sh,.bat"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                processFiles(Array.from(e.target.files));
              }
            }}
          />
        </div>

        {formik.errors.files && typeof formik.errors.files === 'string' && (
          <p className="text-red-500 text-sm mb-4">{formik.errors.files}</p>
        )}

        {/* File List */}
        <div className="flex flex-wrap gap-4 mb-6">
          {formik.values.files.map((upload, index) => (
            <div
              key={index}
              className="bg-[#1c1c1c] border border-gray-700 p-3 rounded-lg flex items-center justify-between w-full sm:w-[48%]"
            >
              <div className="flex items-center gap-3">
                <div className="bg-red-600 text-white px-2 py-1 rounded font-mono text-sm">.{upload.file.name.split('.').pop()}</div>
                <div>
                  <p className="text-sm font-medium">{upload.file.name}</p>
                  <p className="text-xs text-gray-400">
                    {(upload.file.size / 1024).toFixed(0)} kb &nbsp;
                    <span className={upload.status === 'Completed' ? 'text-green-400' : 'text-green-300 animate-pulse'}>
                      ● {upload.status}
                    </span>
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => removeFile(index)} className="text-white hover:text-red-400">
                {upload.status === 'Completed' ? <FaTrash /> : <FaTimes />}
              </button>
            </div>
          ))}
        </div>

        {/* Script Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Script Name *</label>
          <input
            type="text"
            name="scriptName"
            placeholder="Enter script name"
            className="w-full p-2 rounded bg-[#1c1c1c] border border-gray-700 focus:outline-none"
            value={formik.values.scriptName}
            onChange={formik.handleChange}
          />
          {formik.touched.scriptName && formik.errors.scriptName && <p className="text-red-500 text-sm mt-1">{formik.errors.scriptName}</p>}
        </div>

        {/* Script Description */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">Script Description *</label>
          <textarea
            name="scriptDescription"
            rows={3}
            placeholder="Enter script description"
            className="w-full p-2 rounded bg-[#1c1c1c] border border-gray-700 focus:outline-none"
            value={formik.values.scriptDescription}
            onChange={formik.handleChange}
          />
          {formik.touched.scriptDescription && formik.errors.scriptDescription && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.scriptDescription}</p>
          )}
        </div>

        {isSuccess && (
          <div className="flex items-center p-4 text-green-600 bg-green-100 rounded">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>Script {isEdit ? 'update' : 'creation'} completed successfully!</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mt-6">
          <button
            type="button"
            style={closeBtnStyle}
            className="hover:bg-white hover:text-black transition"
            onClick={() => navigate('/ScriptAutomation')}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{
              ...nextBtnStyle,
              opacity: isSubmitting ? 0.7 : 1,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (isEdit ? 'Updating...' : 'Uploading...') : isEdit ? 'Update Script' : 'Upload Script'}
          </button>
        </div>
      </form>
    </div>
  );
};

const closeBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #555',
  borderRadius: '2rem',
  cursor: 'pointer',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

const nextBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#b31317',
  color: '#fff',
  border: 'none',
  borderRadius: '2rem',
  cursor: 'pointer',
  fontWeight: 'bold',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

export default UploadScriptForm;
